import { ErrorHandler, inject, Injectable } from '@angular/core';
import { WA_WINDOW } from '@ng-web-apis/common';
import { PostHogService } from './posthog.service';
import { POSTHOG_EXCEPTION_CONTEXT } from './posthog-exception-context.token';

const STALE_CHUNK_MESSAGES = [
  /ChunkLoadError: Loading chunk/,
  /Importing a module script failed/,
  /Failed to fetch dynamically imported module/,
];

/** Angular hands the handler whatever was thrown, which is not always an Error. */
// eslint-disable-next-line no-restricted-syntax -- the whole point of the function is that Angular hands the handler whatever was thrown
function describe(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  // SAFETY(legacy): unreviewed, pre-dates this rule.
  const candidate = value as { message?: unknown; name?: unknown } | null | undefined;

  return [candidate?.name, candidate?.message].filter((part) => typeof part === 'string').join(': ');
}

@Injectable()
export class PostHogErrorHandler implements ErrorHandler {
  private readonly posthogService = inject(PostHogService);
  private readonly window = inject<Window>(WA_WINDOW);
  private readonly context = inject(POSTHOG_EXCEPTION_CONTEXT, { optional: true });

  public handleError(error: unknown): void {
    // SAFETY(legacy): unreviewed, pre-dates this rule.
    const originalError = (error as { originalError?: unknown } | null | undefined)?.originalError;
    const reported = originalError ?? error;

    // Read both: Angular's wrapper carries the message, the original carries the name, and a chunk
    // failure can present as either depending on how it was thrown.
    const description = `${describe(error)} ${describe(originalError)}`;

    this.report(reported, error);

    // A deploy replaces the hashed chunks, so a session that started on the old build 404s on its
    // next lazy route. Reloading picks up the new index; without it the user sees a dead screen.
    // Reported first, because navigation aborts anything still queued.
    if (STALE_CHUNK_MESSAGES.some((pattern) => pattern.test(description))) {
      this.window.location.reload();
    }
  }

  // eslint-disable-next-line no-restricted-syntax -- both the unwrapped and the original throwable reach PostHog untyped, for the same reason
  private report(reported: unknown, original: unknown): void {
    const client = this.posthogService.client;

    if (!client) {
      console.error(original);
      return;
    }

    const properties = this.context?.();

    // Absent rather than undefined, so an app with no context reports exactly as before.
    if (properties) {
      client.captureException(reported, properties);

      return;
    }

    client.captureException(reported);
  }
}
